import { useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { Check, ChevronDown } from 'lucide-react-native';
import { colors, LANGUAGE_NAMES } from '@hogar/shared';
import { Segmented, Txt, useT } from './ui';

/**
 * One language picker for the whole app (Mike, 2026-10-15): a toggle when there are two languages,
 * a list when there are more (or when `list` is set, as for a worker's language, which will grow).
 * Each language is named in itself: Español, English, Polski.
 */
export function LanguagePicker<T extends string>({ value, options, onChange, list }: { value: T; options: readonly T[]; onChange: (v: T) => void; list?: boolean }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (!list && options.length <= 2) {
    return <Segmented<T> value={value} options={options.map((o) => ({ value: o, label: LANGUAGE_NAMES[o] ?? o }))} onChange={onChange} />;
  }
  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('chooseLanguage')}
        style={{ flexDirection: 'row', alignItems: 'center', minHeight: 48, paddingHorizontal: 14, borderRadius: 14, borderWidth: 1, borderColor: colors.cardBorder, backgroundColor: colors.card }}
      >
        <Txt style={{ flex: 1 }}>{LANGUAGE_NAMES[value] ?? value}</Txt>
        <ChevronDown size={18} color={colors.textMuted} />
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable onPress={() => setOpen(false)} style={{ flex: 1, backgroundColor: 'rgba(43,53,48,0.45)', justifyContent: 'center', padding: 24 }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 18, paddingVertical: 8, maxWidth: 360, width: '100%', alignSelf: 'center' }}>
            <Txt weight="semibold" style={{ paddingHorizontal: 18, paddingVertical: 10 }}>
              {t('chooseLanguage')}
            </Txt>
            {options.map((o) => (
              <Pressable
                key={o}
                onPress={() => {
                  onChange(o);
                  setOpen(false);
                }}
                style={{ flexDirection: 'row', alignItems: 'center', minHeight: 52, paddingHorizontal: 18, borderTopWidth: 1, borderTopColor: colors.divider }}
              >
                <Txt style={{ flex: 1 }} weight={o === value ? 'semibold' : 'regular'}>
                  {LANGUAGE_NAMES[o] ?? o}
                </Txt>
                {o === value && <Check size={18} color={colors.primary} />}
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
    </>
  );
}
